import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { CiCircleChevDown, CiCircleChevUp } from 'react-icons/ci'
import { FaBuilding, FaCalendarAlt, FaUserTie, FaLock, FaTag, FaGlobe } from 'react-icons/fa'
import PropTypes from 'prop-types'

const OrgProjectCard = ({ data }) => {
  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <motion.article
      className="org__card"
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      whileHover={{ y: -5, transition: { duration: 0.25 } }}
    >
      <div className="org__card-accent" />

      <div className="org__card-header">
        <div className="org__card-badges">
          {data.type && (
            <span className="org__badge org__badge--type"><FaTag /> {data.type}</span>
          )}
          {data.domain && (
            <span className="org__badge org__badge--domain"><FaGlobe /> {data.domain}</span>
          )}
        </div>
        <span className="org__private-badge"><FaLock /> Private</span>
      </div>

      <div className="org__card-title-row">
        <h3 className="org__card-title">{data.title}</h3>
        <motion.button
          className="org__card-toggle"
          onClick={() => setIsExpanded(!isExpanded)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <span>{isExpanded ? <CiCircleChevUp /> : <CiCircleChevDown />}</span>
          <span>{isExpanded ? 'Less' : 'More'}</span>
        </motion.button>
      </div>

      <div className="org__card-meta">
        <span><FaBuilding /> {data.organization}</span>
        {data.year && <span><FaCalendarAlt /> {data.year}</span>}
        {data.role && <span><FaUserTie /> {data.role}</span>}
      </div>

      <p className="org__card-short-desc">{data.shortDescription}</p>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: 'easeInOut' }}
            style={{ overflow: 'hidden' }}
          >
            <div className="org__card-expanded">
              {data.description && (
                <div className="org__card-section">
                  <h4>About the Project</h4>
                  <p>{data.description}</p>
                </div>
              )}
              {data.myContribution && data.myContribution.length > 0 && (
                <div className="org__card-section">
                  <h4>My Contribution</h4>
                  <ul className="org__card-list">
                    {data.myContribution.map((item, i) => (
                      <motion.li key={i} initial={{ opacity: 0, x: -15 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.08 }}>
                        <span className="org__bullet" />{item}
                      </motion.li>
                    ))}
                  </ul>
                </div>
              )}
              {data.tools_tech && data.tools_tech.length > 0 && (
                <div className="org__card-section">
                  <h4>Tech Stack</h4>
                  <div className="org__tags">
                    {data.tools_tech.map((tool, i) => (
                      <motion.span key={tool} className="org__tag" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.06 }}>
                        {tool}
                      </motion.span>
                    ))}
                  </div>
                </div>
              )}
              {data.impact && (
                <div className="org__card-impact">
                  <h4>Impact</h4>
                  <p>{data.impact}</p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="org__card-footer">
        <span className="org__confidential">🔒 Organizational — Confidential</span>
      </div>
    </motion.article>
  )
}

OrgProjectCard.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    organization: PropTypes.string.isRequired,
    year: PropTypes.string,
    role: PropTypes.string,
    type: PropTypes.string,
    domain: PropTypes.string,
    shortDescription: PropTypes.string.isRequired,
    description: PropTypes.string,
    myContribution: PropTypes.arrayOf(PropTypes.string),
    tools_tech: PropTypes.arrayOf(PropTypes.string),
    impact: PropTypes.string,
  }).isRequired
}

export default OrgProjectCard